import { z } from 'zod';

export const Value = z.union([
  z.string(),
  z.number(),
  z.array(z.number()),
  z.boolean(),
  z.null(),
]);
export type Value = z.infer<typeof Value>;

export const SchemaItem = z.object({
  name: z.string(),
  title: z.string(),
  defaultValue: Value,
  description: z.string().optional(),
  type: z.string().optional(),
  recommendation: z.string().optional(),
  visible: z.boolean(),
  readOnly: z.boolean(),
});

export type SchemaItem = z.infer<typeof SchemaItem>;

export const DetectorType = z.union([
  z.literal('HttpStatus'),
  z.literal('NewErrorInFunction'),
  z.literal('Slo'),
  z.literal('FlowDegradation'),
  z.literal('None'),
]);
export type DetectorType = z.infer<typeof DetectorType>;

export const Rule = z.object({
  id: z.string(),
  name: z.string(),
  shortDescription: z.string(),
  fullDescription: z.string(),
  configDescriptionTemplate: z.string(),
  enabled: z.boolean(),
  visible: z.boolean(),
  detector: DetectorType,
  rateInMinutes: z.number().optional(),
  offsetsFromDeploymentInMinutes: z.array(z.number()).optional(),
  dedupKey: z.string().optional(),
  cooldownMs: z.number(),
  schema: z.array(SchemaItem),
});

export type Rule = z.infer<typeof Rule>;

export const Config = z.record(z.string(), Value);

export type Config = z.infer<typeof Config>;

export const RuleConfig = z.object({
  id: z.string(),
  ruleId: z.string(),
  enabled: z.boolean(),
  visible: z.boolean().nullable(),
  accountId: z.string(),
  serviceIds: z.array(z.number()),
  entityServiceId: z.number().nullable(),
  entityIds: z.array(z.number()),
  config: Config,
  lastChangedAt: z.coerce.date(),
});

export type RuleConfig = z.infer<typeof RuleConfig>;

export const EffectiveConfig = RuleConfig.pick({
  ruleId: true,
  enabled: true,
  config: true,
});
export type EffectiveConfig = z.infer<typeof EffectiveConfig>;

export const DetectionReport = z.object({
  createdAt: z.date(),
  accountId: z.string(),
  environmentName: z.string(),
  ruleId: z.string(),
  serviceId: z.number(),
  detectedIssues: z.number(),
  effectiveServiceConfig: EffectiveConfig,
  overrideEntitiesConfig: z.record(z.number(), EffectiveConfig),
  globallyExcludedEndpointsIds: z.array(z.number()).nullable(),
});

export type DetectionReport = z.infer<typeof DetectionReport>;

export const Entity = z.object({
  type: z.union([
    z.literal('function'),
    z.literal('endpoint'),
    z.literal('kafka'),
    z.literal('sqs'),
  ]),
  id: z.number(),
  details: z.unknown(),
});

export const EndpointInvestigationExemplar = z.object({
  type: z.literal('endpoint_investigation'),
  s3Pointer: z.string(),
  timestamp: z.date(),
  exceptions: z.array(
    z.object({
      name: z.string(),
      functionIds: z.array(z.number()),
    }),
  ),
  functionUuidToId: z.record(z.string(), z.number()),
});

export type EndpointInvestigationExemplar = z.infer<typeof EndpointInvestigationExemplar>;
export const Exemplar = EndpointInvestigationExemplar;
export type Exemplar = z.infer<typeof Exemplar>;

export type Entity = z.infer<typeof Entity>;

export const DetectedIssue = z.object({
  accountId: z.string(),
  environmentName: z.string(),
  serviceId: z.number(),
  rule: Rule,
  effectiveServiceConfig: EffectiveConfig,
  overrideEntitiesConfig: z.record(z.number(), EffectiveConfig),
  mainEntity: Entity,
  sources: z.array(Entity),
  detectedAt: z.date(),
  exemplars: z.array(Exemplar).optional().default([]),
});

export type DetectedIssue = z.infer<typeof DetectedIssue>;

export const Service = z.object({
  id: z.number(),
  accountId: z.string(),
});

export type Service = z.infer<typeof Service>;

export const Deployment = z.object({
  accountId: z.string(),
  environmentName: z.string(),
  serviceId: z.number(),
  deploymentTime: z.date(),
});

export type Deployment = z.infer<typeof Deployment>;
